// @flow

import _ from 'lodash';
import Q from 'q';
import type { QPromise } from 'types/q';

import { default as serverBase } from 'server2';
import type { ServerMethodOptions, ServerResponse } from 'server';

import debug from 'debug';


debug.define({
	'splunkLogger': {
		'consoleLog': 'boolean',
	},
});

import type { AuthContextInterface } from 'auth-context';
import type { LogContextInterface } from 'log-context';

import type {
	SplunkValidationEvent,
	SplunkRecord,
	SplunkLoggerInterface,
} from './js/types';

export type {
	SplunkValidationEvent,
	SplunkRecord,
	SplunkLoggerInterface,
};

const DEBOUNCE_CALLS_DELAY = 100;
const RATE_LIMIT_REQUESTS_DELAY = 1000;

export class AbstractBatchedSplunkLogger implements SplunkLoggerInterface {
	_running: QPromise<any> = Q();
	_pendingRecords: Array<SplunkRecord> = [];

	_postNextBatchDebounced: () => void;

	_serverMyApi: typeof serverBase.my.api = serverBase.my.api;

	constructor () {
		this._postNextBatchDebounced = _.debounce(
			this._postNextBatch.bind(this),
			DEBOUNCE_CALLS_DELAY
		);
	}

	withAuthContext (authContext: AuthContextInterface): SplunkLoggerInterface {
		const logger = new this.constructor();
		logger._serverMyApi = this._serverMyApi.withAuthContext(authContext);
		return logger;
	}

	withLogContext (logContext: LogContextInterface): SplunkLoggerInterface {
		const logger = new this.constructor();
		logger._serverMyApi = this._serverMyApi.withLogContext(logContext);
		return logger;
	}

	withFetch (fetch: any): SplunkLoggerInterface {
		const logger = new this.constructor();
		logger._serverMyApi = this._serverMyApi.withFetch(fetch);
		return logger;
	}

	// eslint-disable-next-line no-unused-vars
	_serverPost (options: ServerMethodOptions): QPromise<ServerResponse> {
		throw new Error("Not implemented (abstract)");
	}

	_post (records: Array<SplunkRecord>): QPromise<any> {
		return Q.all([
			this._serverPost({
				body: records,
			}),
			Q.delay(RATE_LIMIT_REQUESTS_DELAY),
		]);
	}

	_postNextBatch () {
		if (this._pendingRecords.length === 0) {
			return this._running;
		}

		const batch = this._pendingRecords;
		this._pendingRecords = [];

		const post = () => this._post(batch)
			.finally(() => {
				this._postNextBatch();
			});

		this._running = this._running.finally(post);

		this._running.done();

		return this._running;
	}

	push (record: SplunkRecord): void {
		this._pendingRecords.push(record);

		if (this._running.inspect().state !== 'pending') {
			this._postNextBatchDebounced();
		}

		if (debug.get('splunkLogger.consoleLog')) {
			const getGuid = path => {
				const v = _.get(record, path);
				if (_.isString(v)) {
					return v.split('-')[0];
				}
				return v;
			};
			console.info(
				'splunkLogger.push',
				getGuid('message.ext.SessionId'),
				getGuid('message.CorrelationId'),
				record.message.BusinessOperationCode,
				record.message.Message,
				record
			);
		}
	}

	flush () {
		return this._postNextBatch();
	}
}

export class OsagoSplunkLogger extends AbstractBatchedSplunkLogger {
	_serverPost (options: ServerMethodOptions): QPromise<ServerResponse> {
		return this._serverMyApi
			.withAddedMiddleware(request => {
				_.unset(request.options.headers, ['x-splunk-log-sessionid']);
				return _.identity;
			})
			.post(['eosago', 'saveSplunkLog'], options);
	}
}

export class OsagoJuridicalSplunkLogger extends AbstractBatchedSplunkLogger {

	companyID: string;

	constructor (companyID: string) {
		super(...arguments);
		this.companyID = companyID;
	}

	_serverPost (options: ServerMethodOptions): QPromise<ServerResponse> {
		return this._serverMyApi
			.withAddedMiddleware(request => {
				_.unset(request.options.headers, ['x-splunk-log-sessionid']);
				return _.identity;
			})
			.post(['eosago', 'saveJuridicalSplunkLog'],
			{...options, query: {...options.query, companyID: this.companyID}});
	}
}

export class LkSplunkLogger extends AbstractBatchedSplunkLogger {
	_serverPost (options: ServerMethodOptions): QPromise<ServerResponse> {
		return this._serverMyApi
			.withAddedMiddleware(request => {
				_.unset(request.options.headers, ['x-splunk-log-sessionid']);
				return _.identity;
			})
			.post(['lk', 'saveSplunkLog'], options);
	}
}

export { noopLogger } from './js/noop-logger';
